/**
 * Design tokens for the gift voucher shop.
 *
 * The palette is paper, ink and one hairline — the venue's brand colour is the only
 * saturated thing on screen and it carries three elements: the primary button, the
 * voucher's top rule and the small caps "Select" label.
 *
 * Every token has a design default, but anything the venue configured in
 * Settings › Gift vouchers › Branding wins over that default. The API always sends a
 * branding block, so `resolveShopTheme` is the single place that decides what the shop,
 * the voucher card, the confirmation and the voucher page are painted with.
 */
import { getContrastColor, withAlpha } from '@shared/utils/checkoutBranding';

/** Branding block as returned by GET /gift-vouchers/shop/:locationId. */
export interface ShopBranding {
  primaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  headerBackgroundColor?: string;
  headerTextColor?: string;
  logoUrl?: string;
  logoWidth?: number;
  logoHeight?: number;
  borderRadius?: number;
  fontFamily?: string;
}

/** Design defaults — used wherever the venue has not set a value in the settings. */
export const DESIGN_TOKENS = {
  /** Page background. */
  paper: '#F6F4F1',
  /** Cards, inputs, preset tiles. */
  surface: '#FFFFFF',
  /** The voucher's code stub, one shade off white. */
  stub: '#FCFBF9',
  /** Headings, amounts, anything that must read first. */
  ink: '#1B1A17',
  /** Body copy. */
  body: '#57534B',
  /** Secondary copy. */
  muted: '#6E6A62',
  /** Small caps labels. */
  label: '#8A857B',
  /** Terms, serial numbers, anything that should recede. */
  faint: '#B2ABA0',
  /** Section rules and card borders. */
  hairline: '#E4E0D9',
  /** Rules inside a card. */
  hairlineSoft: '#EDE9E2',
  /** Input and secondary button borders. */
  inputBorder: '#D8D2C9',
  /** The voucher's perforation. */
  perforation: '#DCD7CE',
  /** Brand accent. */
  accent: '#fa7864',
  /** Positive states — "Paid", "Active". */
  positive: '#3F6B4F',
  positiveSurface: '#EEF3EE',
  positiveBorder: '#D6E2D6',
  /** Corner radius. Small enough to read as printed matter rather than an app. */
  radius: 3,
  fontFamily: "'DM Sans', ui-sans-serif, system-ui, -apple-system, sans-serif",
  monoFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

export interface ShopTheme {
  paper: string;
  surface: string;
  stub: string;
  ink: string;
  body: string;
  muted: string;
  label: string;
  faint: string;
  hairline: string;
  hairlineSoft: string;
  inputBorder: string;
  perforation: string;
  accent: string;
  /** Text that sits on top of `accent`. */
  accentText: string;
  /** Translucent accent, for the rare soft fill. */
  accentSoft: string;
  positive: string;
  positiveSurface: string;
  positiveBorder: string;
  radius: number;
  fontFamily: string;
  monoFamily: string;
  logoUrl: string | null;
  logoWidth: number;
  logoHeight: number;
}

/** Legacy shop defaults that the API used to send. Treated as "not configured". */
const LEGACY_DEFAULTS = new Set(['#f5f5f5', '#111827', '#ffffff']);

function configured(value?: string | null): string | null {
  if (!value || typeof value !== 'string') return null;
  const hex = value.trim().toLowerCase();
  if (!hex) return null;
  return LEGACY_DEFAULTS.has(hex) ? null : value.trim();
}

export function resolveShopTheme(branding?: ShopBranding | null): ShopTheme {
  const d = DESIGN_TOKENS;
  const accent = configured(branding?.primaryColor) || d.accent;
  const paper = configured(branding?.backgroundColor) || d.paper;
  const ink = configured(branding?.textColor) || d.ink;

  return {
    paper,
    surface: d.surface,
    stub: d.stub,
    ink,
    body: d.body,
    muted: d.muted,
    label: d.label,
    faint: d.faint,
    hairline: d.hairline,
    hairlineSoft: d.hairlineSoft,
    inputBorder: d.inputBorder,
    perforation: d.perforation,
    accent,
    accentText: configured(branding?.headerTextColor) || getContrastColor(accent),
    accentSoft: withAlpha(accent, 0.12),
    positive: d.positive,
    positiveSurface: d.positiveSurface,
    positiveBorder: d.positiveBorder,
    radius: typeof branding?.borderRadius === 'number' ? Math.min(branding.borderRadius, 8) : d.radius,
    fontFamily: branding?.fontFamily || d.fontFamily,
    monoFamily: d.monoFamily,
    logoUrl: branding?.logoUrl || null,
    logoWidth: branding?.logoWidth || 180,
    logoHeight: branding?.logoHeight || 60,
  };
}

/**
 * CSS custom properties for a theme, spread onto the outermost element of a page so
 * that Tailwind arbitrary values (`bg-[var(--gv-paper)]`) pick the venue's colours up.
 */
export function themeCssVars(theme: ShopTheme): React.CSSProperties {
  return {
    '--gv-paper': theme.paper,
    '--gv-surface': theme.surface,
    '--gv-stub': theme.stub,
    '--gv-ink': theme.ink,
    '--gv-body': theme.body,
    '--gv-muted': theme.muted,
    '--gv-label': theme.label,
    '--gv-faint': theme.faint,
    '--gv-hairline': theme.hairline,
    '--gv-hairline-soft': theme.hairlineSoft,
    '--gv-input-border': theme.inputBorder,
    '--gv-perforation': theme.perforation,
    '--gv-accent': theme.accent,
    '--gv-accent-text': theme.accentText,
    '--gv-accent-soft': theme.accentSoft,
    '--gv-radius': `${theme.radius}px`,
    fontFamily: theme.fontFamily,
    backgroundColor: theme.paper,
    color: theme.ink,
  } as React.CSSProperties;
}
